import { FormLabel, Grid } from "@material-ui/core";
import * as _ from "lodash";
import * as React from "react";
import { Range } from "react-input-range";

import { CustomerGroupFilterState } from "../../types/index";
import SelectMenu from "../generic/SelectMenu";
import Slider from "../Slider";
import { FilterGroup, preselections } from "./constants";
import FilterContainer from "./FilterContainer";

interface Props {
  customerValueAnalysisFilter: Pick<
    CustomerGroupFilterState,
    | "maxClv"
    | "minClv"
    | "maxChurn"
    | "minChurn"
    | "maxChurnDelta"
    | "minChurnDelta"
  >;
  enabled: boolean;
  changeCustomerGroupFilter: (
    payload: Partial<CustomerGroupFilterState>
  ) => void;
  handleEnabledChange: (filterGroup: FilterGroup, enabled: boolean) => void;
}

class CustomerValueAnalysis extends React.Component<Props> {
  public render(): JSX.Element {
    return (
      <FilterContainer
        enabled={this.props.enabled}
        title="Kundenwertanalyse"
        onEnabledChange={(enabled) => {
          this.props.handleEnabledChange(
            FilterGroup.customerValueAnalysis,
            enabled
          );
        }}
      >
        {(disabled?: boolean) => (
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12}>
              <Grid container justify="flex-start" spacing={2}>
                <Grid item xs={7}>
                  <FormLabel component="div">CLV</FormLabel>
                  <Slider
                    disabled={disabled}
                    minValue={0}
                    maxValue={10}
                    onChange={this.handleClvChange}
                    value={{
                      max: this.props.customerValueAnalysisFilter.maxClv,
                      min: this.props.customerValueAnalysisFilter.minClv,
                    }}
                  />
                </Grid>
                <Grid item>
                  <SelectMenu
                    disabled={disabled}
                    inputLabel="Vorlagen"
                    value={this.getClvPreselectionValue()}
                    items={preselections.clv}
                    onChange={this.handleClvPreselectionChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item={true} xs={12}>
              <Grid container justify="flex-start" spacing={2}>
                <Grid item xs={7}>
                  <FormLabel component="div">Churn Score</FormLabel>
                  <Slider
                    disabled={disabled}
                    minValue={1}
                    maxValue={10}
                    onChange={this.handleChurnChange}
                    value={{
                      max: this.props.customerValueAnalysisFilter.maxChurn,
                      min: this.props.customerValueAnalysisFilter.minChurn,
                    }}
                  />
                </Grid>
                <Grid item>
                  <SelectMenu
                    disabled={disabled}
                    inputLabel="Vorlagen"
                    value={this.getChurnScorePreselectionValue()}
                    items={preselections.churnScore}
                    onChange={this.handleChurnScorePreselectionChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item={true} xs={12}>
              <Grid container justify="flex-start" spacing={2}>
                <Grid item xs={7}>
                  <FormLabel component="div">
                    Churn Score Delta (letzte 30 Tage)
                  </FormLabel>
                  <Slider
                    disabled={disabled}
                    minValue={-9}
                    maxValue={9}
                    onChange={this.handleChurnDeltaChange}
                    value={{
                      max: this.props.customerValueAnalysisFilter.maxChurnDelta,
                      min: this.props.customerValueAnalysisFilter.minChurnDelta,
                    }}
                    step={0.5}
                  />
                </Grid>
                <Grid item>
                  <SelectMenu
                    disabled={disabled}
                    inputLabel="Vorlagen"
                    value={this.getChurnDeltaPreselectionValue()}
                    items={preselections.churnDelta}
                    onChange={this.handleChurnDeltaPreselectionChange}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </FilterContainer>
    );
  }
  private getClvPreselectionValue = () => {
    const currentClvRangeFilter = {
      min: this.props.customerValueAnalysisFilter.minClv,
      max: this.props.customerValueAnalysisFilter.maxClv,
    };
    const matchWithPreselections = _.find(preselections.clv, {
      value: currentClvRangeFilter,
    });
    return matchWithPreselections ? matchWithPreselections.value : "";
  };

  private getChurnScorePreselectionValue = () => {
    const currentChurnScoreRangeFilter = {
      min: this.props.customerValueAnalysisFilter.minChurn,
      max: this.props.customerValueAnalysisFilter.maxChurn,
    };
    const matchWithPreselections = _.find(preselections.churnScore, {
      value: currentChurnScoreRangeFilter,
    });
    return matchWithPreselections ? matchWithPreselections.value : "";
  };

  private getChurnDeltaPreselectionValue = () => {
    const currentChurnDeltaRangeFilter = {
      min: this.props.customerValueAnalysisFilter.minChurnDelta,
      max: this.props.customerValueAnalysisFilter.maxChurnDelta,
    };
    const matchWithPreselections = _.find(preselections.churnDelta, {
      value: currentChurnDeltaRangeFilter,
    });
    return matchWithPreselections ? matchWithPreselections.value : "";
  };

  private handleClvChange = (range: Range) => {
    if (range) {
      this.props.changeCustomerGroupFilter({
        maxClv: range.max,
        minClv: range.min,
      });
    } else {
      this.props.changeCustomerGroupFilter({
        maxClv: 10,
        minClv: 0,
      });
    }
  };

  private handleChurnChange = (range: Range) => {
    if (range) {
      this.props.changeCustomerGroupFilter({
        maxChurn: range.max,
        minChurn: range.min,
      });
    } else {
      this.props.changeCustomerGroupFilter({
        maxChurn: 10,
        minChurn: 1,
      });
    }
  };

  private handleChurnDeltaChange = (range: Range) => {
    if (range) {
      this.props.changeCustomerGroupFilter({
        maxChurnDelta: _.round(range.max, 1),
        minChurnDelta: _.round(range.min, 1),
      });
    } else {
      this.props.changeCustomerGroupFilter({
        maxChurnDelta: 9,
        minChurnDelta: -9,
      });
    }
  };

  private handleChurnScorePreselectionChange = (
    event: React.ChangeEvent<{ value: any }>
  ) => {
    const range = event.target.value;
    this.handleChurnChange(range);
  };

  private handleChurnDeltaPreselectionChange = (
    event: React.ChangeEvent<{ value: any }>
  ) => {
    const range = event.target.value;
    this.handleChurnDeltaChange(range);
  };

  private handleClvPreselectionChange = (
    event: React.ChangeEvent<{ value: any }>
  ) => {
    const range = event.target.value;
    this.handleClvChange(range);
  };
}

export default CustomerValueAnalysis;



// WEBPACK FOOTER //
// ./src/components/Filters/CustomerValueAnalysisFilter.tsx